
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import CompanyForm from '@/components/CompanyForm';
import { CompanyFormValues } from '@/components/company/CompanyFormSchema';
import { useCompanyData } from '@/hooks/useCompanyData';
import { useCompanyUpdate } from '@/hooks/useCompanyUpdate';
import { CompanyLoading } from './overview/CompanyLoading';
import { CompanyDataDisplay } from './overview/CompanyDataDisplay';
import { CompanyError } from './overview/CompanyError';

export default function CompanyOverview() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { companyData, loading, setCompanyData } = useCompanyData();
  const { handleUpdateCompany, loading: updating } = useCompanyUpdate();
  const { toast } = useToast();

  const onSubmit = async (data: CompanyFormValues) => {
    const success = await handleUpdateCompany(data, companyData, setCompanyData);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleViewCompanyProfile = () => {
    if (companyData?.name) {
      // Create a URL-safe version of the company name
      const encodedName = encodeURIComponent(companyData.name.toLowerCase().replace(/\s+/g, '-'));
      // Open the company profile in a new tab using the correct /companies/ route
      window.open(`/companies/${encodedName}`, '_blank');
    } else {
      toast({
        title: "Unable to open profile",
        description: "Company information is not available.",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Company Overview</h2>
          {!loading && companyData && !isEditing && (
            <Button 
              variant="outline" 
              onClick={() => setIsEditing(true)}
            >
              Edit Company Information
            </Button>
          )}
        </div>

        {loading ? (
          <CompanyLoading />
        ) : companyData && !isEditing ? (
          <CompanyDataDisplay 
            companyData={companyData} 
            handleViewCompanyProfile={handleViewCompanyProfile} 
          />
        ) : isEditing ? (
          <CompanyForm
            onSubmit={onSubmit}
            onCancel={() => setIsEditing(false)}
            submitButtonText="Save Changes"
            initialValues={{
              name: companyData.name,
              location: companyData.locations?.[0] || "",
              bio: companyData.bio || "",
              staffSize: companyData.staff_size || "",
              website: companyData.website || "",
              twitterUrl: companyData.twitter_url || "",
              instagramUrl: companyData.instagram_url || "",
              linkedinUrl: companyData.linkedin_url || ""
            }}
          />
        ) : (
          <CompanyError />
        )}
      </CardContent>
    </Card>
  );
}
